import {
  GLOFOX_SEARCH_INDEX,
  type GlofoxSearchEntry,
} from "@/content/glofox/portal-docs/search-index";
import {
  GLOFOX_API_REFERENCE_HREF,
  GLOFOX_REQUEST_ACCESS_HREF,
} from "@/lib/portal/glofox-routes";

export type { GlofoxSearchEntry };

/** Static destinations that are not markdown pages but still searchable. */
export const GLOFOX_SEARCH_EXTRAS: GlofoxSearchEntry[] = [
  {
    route: GLOFOX_API_REFERENCE_HREF,
    title: "API Reference",
    text: "OpenAPI reference endpoints schemas Redoc API docs",
  },
  {
    route: GLOFOX_REQUEST_ACCESS_HREF,
    title: "Request access",
    text: "Request API access sandbox credentials onboarding API key bearer token oauth",
  },
];

export type SearchGlofoxDocsOptions = {
  limit?: number;
  /** Max results kept per route before filling remaining slots from other pages. */
  perRouteLimit?: number;
  entries?: GlofoxSearchEntry[];
};

export type GlofoxSearchHit = {
  entry: GlofoxSearchEntry;
  score: number;
};

export type GlofoxSearchResponse = {
  hits: GlofoxSearchHit[];
  /** Total matches before applying the display limit. */
  total: number;
  truncated: boolean;
};

const DEFAULT_LIMIT = 8;
const DEFAULT_PER_ROUTE_LIMIT = 2;
const MIN_TOKEN_LENGTH = 2;

/**
 * Normalize and tokenize a query for search and highlighting.
 * Punctuation is treated as whitespace. Prefer tokens of 2+ chars; keep
 * shorter tokens only when the query has no longer token (e.g. `"a"`).
 */
export function tokenizeQuery(query: string): string[] {
  const tokens = query
    .toLowerCase()
    .replace(/[^\p{L}\p{N}_/-]+/gu, " ")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  const longTokens = tokens.filter((token) => token.length >= MIN_TOKEN_LENGTH);
  return longTokens.length > 0 ? longTokens : tokens;
}

function scoreField(haystack: string, token: string, weight: number): number {
  const index = haystack.indexOf(token);
  if (index < 0) {
    return 0;
  }
  // Prefer word-start / prefix matches over mid-word hits.
  const boundary =
    index === 0 || /[\s\-_/]/.test(haystack.charAt(index - 1)) ? 2 : 1;
  return weight * boundary;
}

function scoreEntryAnd(
  entry: GlofoxSearchEntry,
  tokens: string[],
): number {
  const title = entry.title.toLowerCase();
  const section = (entry.section ?? "").toLowerCase();
  const text = entry.text.toLowerCase();

  let total = 0;
  for (const token of tokens) {
    const titleScore = scoreField(title, token, 12);
    const sectionScore = scoreField(section, token, 8);
    const textScore = scoreField(text, token, 2);
    const best = Math.max(titleScore, sectionScore, textScore);
    if (best === 0) {
      return 0;
    }
    total += best;
  }
  // Prefer page-level hits slightly over section hits when scores tie.
  if (!entry.section) {
    total += 1;
  }
  return total;
}

/** Partial (OR) scoring used only when strict AND matching returns nothing. */
function scoreEntryOr(entry: GlofoxSearchEntry, tokens: string[]): number {
  const title = entry.title.toLowerCase();
  const section = (entry.section ?? "").toLowerCase();
  const text = entry.text.toLowerCase();

  let total = 0;
  let matched = 0;
  for (const token of tokens) {
    const titleScore = scoreField(title, token, 12);
    const sectionScore = scoreField(section, token, 8);
    const textScore = scoreField(text, token, 2);
    const best = Math.max(titleScore, sectionScore, textScore);
    if (best > 0) {
      matched += 1;
      total += best;
    }
  }
  if (matched === 0) {
    return 0;
  }
  // Prefer entries that match more tokens; keep OR scores below typical AND hits.
  total = total * matched + matched;
  if (!entry.section) {
    total += 1;
  }
  return total;
}

function diversifyHits(
  ranked: GlofoxSearchHit[],
  limit: number,
  perRouteLimit: number,
): GlofoxSearchHit[] {
  if (ranked.length <= limit && perRouteLimit >= limit) {
    return ranked.slice(0, limit);
  }

  const selected: GlofoxSearchHit[] = [];
  const perRoute = new Map<string, number>();
  const deferred: GlofoxSearchHit[] = [];

  for (const hit of ranked) {
    const count = perRoute.get(hit.entry.route) ?? 0;
    if (count < perRouteLimit) {
      selected.push(hit);
      perRoute.set(hit.entry.route, count + 1);
      if (selected.length >= limit) {
        return selected;
      }
    } else {
      deferred.push(hit);
    }
  }

  for (const hit of deferred) {
    selected.push(hit);
    if (selected.length >= limit) {
      break;
    }
  }

  return selected;
}

function rankCorpus(
  corpus: GlofoxSearchEntry[],
  tokens: string[],
  scorer: (entry: GlofoxSearchEntry, tokens: string[]) => number,
): GlofoxSearchHit[] {
  return corpus
    .map((entry) => ({ entry, score: scorer(entry, tokens) }))
    .filter((row) => row.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.entry.title.localeCompare(b.entry.title) ||
        (a.entry.section ?? "").localeCompare(b.entry.section ?? ""),
    );
}

/**
 * Full-text docs search over the build-time index.
 * Uses AND matching first; falls back to OR when nothing matches.
 * Pass `entries` in tests; production uses the generated index + extras.
 */
export function searchGlofoxDocs(
  query: string,
  options: SearchGlofoxDocsOptions = {},
): GlofoxSearchResponse {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) {
    return { hits: [], total: 0, truncated: false };
  }

  const limit = options.limit ?? DEFAULT_LIMIT;
  const perRouteLimit = options.perRouteLimit ?? DEFAULT_PER_ROUTE_LIMIT;
  const corpus =
    options.entries ?? [...GLOFOX_SEARCH_INDEX, ...GLOFOX_SEARCH_EXTRAS];

  let ranked = rankCorpus(corpus, tokens, scoreEntryAnd);
  if (ranked.length === 0 && tokens.length > 1) {
    ranked = rankCorpus(corpus, tokens, scoreEntryOr);
  }

  const hits = diversifyHits(ranked, limit, perRouteLimit);
  return {
    hits,
    total: ranked.length,
    truncated: ranked.length > hits.length,
  };
}

/** Convenience helper — entries only (for callers that ignore metadata). */
export function searchGlofoxDocEntries(
  query: string,
  options: SearchGlofoxDocsOptions = {},
): GlofoxSearchEntry[] {
  return searchGlofoxDocs(query, options).hits.map((hit) => hit.entry);
}

/**
 * Build a short snippet centered on the first query token match.
 * Falls back to the start of the text when no token appears.
 */
export function snippetAroundMatch(
  text: string,
  query: string,
  maxChars = 160,
): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return "";
  }
  if (normalized.length <= maxChars) {
    return normalized;
  }

  const tokens = tokenizeQuery(query);
  const lower = normalized.toLowerCase();
  let matchIndex = -1;
  for (const token of tokens) {
    const index = lower.indexOf(token);
    if (index >= 0 && (matchIndex < 0 || index < matchIndex)) {
      matchIndex = index;
    }
  }

  if (matchIndex < 0) {
    return `${normalized.slice(0, maxChars).trimEnd()}…`;
  }

  const half = Math.floor(maxChars / 2);
  let start = Math.max(0, matchIndex - half);
  let end = Math.min(normalized.length, start + maxChars);
  if (end - start < maxChars) {
    start = Math.max(0, end - maxChars);
  }

  // Prefer breaking on word boundaries when we truncated.
  if (start > 0) {
    const space = normalized.indexOf(" ", start);
    if (space > start && space < start + 20) {
      start = space + 1;
    }
  }
  if (end < normalized.length) {
    const space = normalized.lastIndexOf(" ", end);
    if (space > end - 20 && space > start) {
      end = space;
    }
  }

  const slice = normalized.slice(start, end).trim();
  const prefix = start > 0 ? "…" : "";
  const suffix = end < normalized.length ? "…" : "";
  return `${prefix}${slice}${suffix}`;
}
